import {apiSlice} from './apiSlice';
import {
    Booking,
    Journey,
    CreateBookingParams,
    CreateBookingType,
    Deployment,
    DeploymentBooking,
    EstimatedCostOriginalResponse,
    EstimatedCostTransformedResponse,
    GetBookingEstimatedCostParams,
    GetBookingsParams,
    GetDeploymentBookingsParams,
    GetDeploymentsByTimeParams,
    GetIsDeploymentAvailableParams,
    TaxType,
    GetBookingsReturnType,
    UpdateBookingResponse,
    UpdateBookingParams,
    DiscountBookingParams,
    BookingDeploymentHistory,
    GetJourneyTripsParams,
    GetJourneyTripsResponse,
    BookingOwner
} from '../../types/bookingTypes';
import dayjs from 'dayjs';

export const bookingInjectedEndpoints = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getBookings: builder.query<GetBookingsReturnType, GetBookingsParams>({
            query: ({
                page = 1,
                pageSize = 10,
                searchType = 'contained_by',
                startedAt = null,
                endedAt = null,
                state = null,
                descSort = 0,
                membershipID
            }) => {
                let queryString = `?page=${page}&per_page=${pageSize}&search_type=${searchType}&desc_sort=${descSort}&membership_id=${membershipID}`;

                if (startedAt) {
                    startedAt = encodeURIComponent(startedAt);
                    queryString += `&booking_started_at=${startedAt}`;
                }

                if (endedAt) {
                    endedAt = encodeURIComponent(endedAt);
                    queryString += `&booking_ended_at=${endedAt}`;
                }

                if (state) {
                    state = encodeURIComponent(state);
                    queryString += `&state=${state}`;
                }

                return `/bookings${queryString}`;
            },
            providesTags: (result) =>
                result ? [...result.data.map(({id}) => ({type: 'Booking', id})), 'Booking'] : ['Booking'],
        }),
        getBooking: builder.query<Booking, number>({
            query: (id) => `/bookings/${id}`,
            providesTags: (result, err, id) => result ? [{type: 'Booking', id}] : ['Booking'],
        }),
        getBookingOwner: builder.query<BookingOwner, number>({
            query: (id) => `/bookings/${id}/booking_owner`
        }),
        getDeploymentBookings: builder.query<DeploymentBooking[], GetDeploymentBookingsParams>({
            query: ({
                deploymentIDs,
                bookingStatus = 'CONFIRMED',
                pageSize = 10,
                pageNumber = 1,
                date
            }) => {
                const formattedDate = encodeURIComponent(dayjs(date).format('YYYY-MM-DD HH:mm:ssZZ'));

                return `/availabilities/deployment_bookings?deployments=${deploymentIDs}&booking_state=${bookingStatus}&per_page=${pageSize}&page=${pageNumber}&date=${formattedDate}`;
            },
            transformResponse: (response: { data: DeploymentBooking[] }) => {
                return response.data;
            },
        }),
        createBooking: builder.mutation<CreateBookingType, CreateBookingParams>({
            query: ({
                deploymentID,
                startedAt,
                bookingType,
                endedAt,
                membershipID
            }) => {
                return {
                    url: '/bookings',
                    method: 'POST',
                    body: {
                        deployment_id: deploymentID,
                        started_at: startedAt.startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ'),
                        type: bookingType,
                        ended_at: endedAt?.startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ'),
                        membership_id: membershipID
                    },
                };
            },
            invalidatesTags: ['Booking', 'Deployments'],
        }),
        cancelBooking: builder.mutation<void, number>({
            query: (id) => {
                const cancelBookingURL = `/bookings/${id}`;
                return {
                    url: cancelBookingURL,
                    method: 'DELETE',
                }
            },
            invalidatesTags: (result, err, arg) => [{type: 'Booking', id: arg}, 'Deployments'],
        }),
        getDeploymentsByTime: builder.query<Deployment[], GetDeploymentsByTimeParams>({
            query: ({
                startedAt = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ssZZ'),
                endedAt = null,
                page = 1,
                pageSize = 999999
            }) => {
                const startedAtString = encodeURIComponent(startedAt.toString());
                const pageString = encodeURIComponent(page.toString());
                const pageSizeString = encodeURIComponent(pageSize.toString());
                let requestUrl = `deployments/time?page=${pageString}&per_page=${pageSizeString}&started_at=${startedAtString}`;

                if (endedAt) {
                    const endedAtString = encodeURIComponent(endedAt.toString());
                    requestUrl += `&ended_at=${endedAtString}`;
                }

                return requestUrl;
            },
            transformResponse: (response: { data: Deployment[] }) => {
                return response.data || [];
            },
            providesTags: ['Booking'],
        }),
        getBookingEstimatedCost: builder.query<EstimatedCostTransformedResponse, GetBookingEstimatedCostParams>({
            query: ({
                startTime,
                endTime,
                membershipID,
                deploymentID
            }) => {
                const startTimeString = encodeURIComponent(startTime.startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ'));
                const endTimeString = encodeURIComponent(endTime.startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ'));

                return `bookings/estimated_costs?started_at=${startTimeString}&ended_at=${endTimeString}&membership_id=${membershipID}&deployment_id=${deploymentID}`;
            },
            transformResponse: (response: EstimatedCostOriginalResponse) => {
                let totalTax = 0;
                let totalCostWithTax = 0;
                let totalCostWithoutTax = 0;

                // Transform taxes object into an array if it exists.
                let taxes: TaxType[] = [];

                if (response.taxes) {
                    taxes = Object.values(response.taxes);
                    taxes.forEach((tax) => {
                        totalTax += tax.amount;
                        totalCostWithTax += tax.amount;
                    });
                }

                totalCostWithoutTax = response.time.amount + response.distance.amount;
                totalCostWithTax += totalCostWithoutTax;

                // Delete the taxes property from the original response object.
                delete response.taxes;

                const transformedCosts = {
                    taxes,
                    totalTax,
                    totalCostWithTax,
                    totalCostWithoutTax,
                    timeCost: response.time.amount,
                    distanceCost: response.distance.amount,
                };

                // Return both transformed and original costs.
                return {
                    transformedCosts,
                    originalCosts: response,
                };
            }

        }),
        getIsDeploymentAvailable: builder.query<string, GetIsDeploymentAvailableParams>({
            query: ({
                deploymentID,
                startTime,
                endTime
            }) => {
                const encodedStartTime = encodeURIComponent(startTime.format('YYYY-MM-DD HH:mm:ssZZ'));
                const encodedEndTime = encodeURIComponent(endTime.format('YYYY-MM-DD HH:mm:ssZZ'));

                return `deployments/${deploymentID}/is_available?started_at=${encodedStartTime}&ended_at=${encodedEndTime}`;
            },
            transformResponse: (response: string) => {
                return response ? 'AVAILABLE' : 'UNAVAILABLE';
            },
        }),
        updateBooking: builder.mutation<UpdateBookingResponse, UpdateBookingParams>({
            query: ({ bookingID, requestData }) => {
                const bookingUpdateURL = `/bookings/${bookingID}`;
                return {
                    url: bookingUpdateURL,
                    method: 'PUT',
                    body: requestData,
                };
            },
            invalidatesTags: (result, err, arg) => [{type: 'Booking', id: arg.bookingID}, 'Deployments'],
        }),
        discountBooking: builder.mutation<void, DiscountBookingParams>({
            query: ({bookingID, type, portion, amount, percent}) => {
                return {
                    url: `/bookings/${bookingID}/discount`,
                    method: 'POST',
                    body: {
                        portion,
                        type,
                        amount,
                        percent
                    }
                }
            },
            invalidatesTags: (result, error, arg) => [{type: 'Booking', id: arg.bookingID}]
        }),
        getBookingDeploymentHistory: builder.query<BookingDeploymentHistory[], number>({
            query: (bookingID) => {
                return `/bookings/getBookingDeploymentHistory?booking_id=${bookingID}`;
            },
            providesTags: (result, err, args) => [{type: 'Booking', id: args}],
        }),
        getJourney: builder.query<Journey, number>({
            query: (id) => `/journeys/${id}`,
            providesTags: (result, err, id) => result ? [{type: 'Booking', id: result.booking.id}] : ['Booking'],
        }),
        getJourneyTrips: builder.query<GetJourneyTripsResponse, GetJourneyTripsParams>({
            query: ({
                journeyID,
                page = 1,
                perPage = 999999
            }) => {
                return `/journeys/${journeyID}/trips/?page=${page}&per_page=${perPage}`;
            }
        })
    })
});

export const bookingEndpoints = bookingInjectedEndpoints.endpoints;
export const {
    useLazyGetBookingsQuery,
    useGetBookingQuery,
    useGetBookingOwnerQuery,
    useGetJourneyQuery,
    useLazyGetDeploymentBookingsQuery,
    useCreateBookingMutation,
    useCancelBookingMutation,
    useGetDeploymentsByTimeQuery,
    useLazyGetBookingEstimatedCostQuery,
    useLazyGetIsDeploymentAvailableQuery,
    useUpdateBookingMutation,
    useDiscountBookingMutation,
    useGetBookingDeploymentHistoryQuery,
    useGetJourneyTripsQuery,
} = bookingInjectedEndpoints;
